import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Form, Alert, Badge, Spinner, InputGroup } from 'react-bootstrap';
import { FaSearch, FaUndo, FaCheckCircle, FaExclamationTriangle, FaClipboardList } from 'react-icons/fa';

const RestoreArchivedAssessmentsModal = ({ 
  show, 
  onHide, 
  schoolid,
  teacherid,
  teacherName,
  getArchivedAssessmentsBySchool,
  restoreArchivedAssessment,
  batchRestoreArchivedAssessments
}) => {
  const [archivedAssessments, setArchivedAssessments] = useState([]);
  const [filteredAssessments, setFilteredAssessments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAssessments, setSelectedAssessments] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [includeRestored, setIncludeRestored] = useState(false);
  const [timeFilter, setTimeFilter] = useState('all');
  const [academicYearFilter, setAcademicYearFilter] = useState('all');
  const [restoring, setRestoring] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (show && schoolid) {
      loadArchivedAssessments();
    }
  }, [show, schoolid, includeRestored]);

  useEffect(() => {
    filterAssessments();
  }, [archivedAssessments, searchTerm, timeFilter, academicYearFilter]);

  const loadArchivedAssessments = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await getArchivedAssessmentsBySchool(schoolid, includeRestored, null, null, 500, 0);
      setArchivedAssessments(response.data || []);
    } catch (error) {
      setMessage({ type: 'danger', text: `Failed to load archived assessments: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const filterAssessments = () => {
    let filtered = [...archivedAssessments];
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(assessment => 
        assessment.studentName?.toLowerCase().includes(search) ||
        assessment.assessmentName?.toLowerCase().includes(search) ||
        assessment.screeningPoint?.toLowerCase().includes(search) ||
        assessment.id?.toLowerCase().includes(search)
      );
    }

    if (academicYearFilter !== 'all') {
      filtered = filtered.filter(assessment => assessment.academicYear === academicYearFilter);
    }

    if (timeFilter !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      
      switch(timeFilter) {
        case 'today':
          cutoff.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoff.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoff.setDate(now.getDate() - 30);
          break;
      }
      
      filtered = filtered.filter(assessment => new Date(assessment.archivedDate) >= cutoff);
    }

    setFilteredAssessments(filtered);
  };

  const getUniqueAcademicYears = () => {
    const years = new Set(archivedAssessments.map(a => a.academicYear).filter(Boolean));
    return Array.from(years).sort().reverse();
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const unrestoredIds = filteredAssessments.filter(a => !a.restored).map(a => a.id);
      setSelectedAssessments(new Set(unrestoredIds));
    } else {
      setSelectedAssessments(new Set());
    }
  };

  const handleSelectAssessment = (assessmentId, isRestored) => {
    if (isRestored) return;
    const newSelected = new Set(selectedAssessments);
    if (newSelected.has(assessmentId)) {
      newSelected.delete(assessmentId);
    } else {
      newSelected.add(assessmentId);
    }
    setSelectedAssessments(newSelected);
  };

  const handleRestore = async () => {
    if (selectedAssessments.size === 0) {
      setMessage({ type: 'warning', text: 'Please select at least one assessment to restore' });
      return;
    }
    setShowConfirmation(true);
  };

  const confirmRestore = async () => {
    setRestoring(true);
    setMessage({ type: '', text: '' });
    setShowConfirmation(false);

    try {
      const assessmentIds = Array.from(selectedAssessments);
      
      if (assessmentIds.length === 1) {
        await restoreArchivedAssessment(assessmentIds[0], teacherid, teacherName);
        setMessage({ type: 'success', text: '✓ Assessment restored successfully!' });
      } else {
        const result = await batchRestoreArchivedAssessments(assessmentIds, teacherid, teacherName);
        setMessage({ type: 'success', text: `✓ Successfully restored ${result.restoredCount} assessment(s)!` });
        
        if (result.errors && result.errors.length > 0) {
          setMessage(prev => ({
            ...prev,
            text: prev.text + ` ${result.errors.length} failed.`
          }));
        }
      }

      setSelectedAssessments(new Set());
      await loadArchivedAssessments();
    } catch (error) {
      setMessage({ type: 'danger', text: `Restoration failed: ${error.message}` });
    } finally {
      setRestoring(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaClipboardList className="me-2" />
            Restore Archived Assessments
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {message.text && (
            <Alert variant={message.type} onClose={() => setMessage({ type: '', text: '' })} dismissible>
              {message.text}
            </Alert>
          )}

          <div className="mb-3">
            <div className="row g-2">
              <div className="col-md-5">
                <InputGroup>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                  <Form.Control
                    placeholder="Search by student, assessment, screening point..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </div>
              <div className="col-md-2">
                <Form.Select value={timeFilter} onChange={(e) => setTimeFilter(e.target.value)}>
                  <option value="all">All Time</option>
                  <option value="today">Archived Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                </Form.Select>
              </div>
              <div className="col-md-2">
                <Form.Select value={academicYearFilter} onChange={(e) => setAcademicYearFilter(e.target.value)}>
                  <option value="all">All Years</option>
                  {getUniqueAcademicYears().map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-3">
                <Form.Check
                  type="checkbox"
                  label="Show Already Restored"
                  checked={includeRestored}
                  onChange={(e) => setIncludeRestored(e.target.checked)}
                />
              </div>
            </div>
          </div>

          <div className="mb-3 p-2 bg-light rounded">
            <small className="text-muted">
              Showing {filteredAssessments.length} of {archivedAssessments.length} archived assessments
              {selectedAssessments.size > 0 && ` | ${selectedAssessments.size} selected`}
            </small>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading archived assessments...</p>
            </div>
          ) : filteredAssessments.length === 0 ? (
            <Alert variant="info">
              <FaCheckCircle className="me-2" />
              No archived assessments found. {includeRestored ? 'Try adjusting your filters.' : 'All clean!'}
            </Alert>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table striped bordered hover size="sm">
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <Form.Check
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={selectedAssessments.size > 0 && 
                          selectedAssessments.size === filteredAssessments.filter(a => !a.restored).length}
                      />
                    </th>
                    <th>Student</th>
                    <th>Assessment</th>
                    <th>Screening Point</th>
                    <th>Academic Year</th>
                    <th>Archived</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssessments.map((assessment) => (
                    <tr key={assessment.id} style={{ 
                      opacity: assessment.restored ? 0.6 : 1,
                      backgroundColor: assessment.restored ? '#f8f9fa' : 'white'
                    }}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedAssessments.has(assessment.id)}
                          onChange={() => handleSelectAssessment(assessment.id, assessment.restored)}
                          disabled={!!assessment.restored}
                        />
                      </td>
                      <td>
                        <strong>{assessment.studentName || 'Unknown'}</strong>
                        <br />
                        <small className="text-muted">{assessment.studentid}</small>
                      </td>
                      <td>{assessment.assessmentName || 'N/A'}</td>
                      <td>{assessment.screeningPoint || 'N/A'}</td>
                      <td>{assessment.academicYear || 'N/A'}</td>
                      <td>
                        {formatDate(assessment.archivedDate)}
                        <br />
                        <Badge bg="secondary">{getTimeAgo(assessment.archivedDate)}</Badge>
                      </td>
                      <td>
                        {assessment.restored ? (
                          <Badge bg="success">
                            <FaCheckCircle className="me-1" />
                            Restored
                          </Badge>
                        ) : (
                          <Badge bg="info">
                            <FaExclamationTriangle className="me-1" />
                            Archived
                          </Badge>
                        )}
                        {assessment.restored && (
                          <div className="mt-1">
                            <small className="text-muted">
                              by {assessment.restoredByTeacherName}
                              <br />
                              {formatDate(assessment.restoredDate)}
                            </small>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <div className="w-100 d-flex justify-content-between align-items-center">
            <div>
              {selectedAssessments.size > 0 && (
                <span className="text-muted">
                  {selectedAssessments.size} assessment(s) selected for restoration
                </span>
              )}
            </div>
            <div>
              <Button variant="secondary" onClick={onHide} className="me-2">
                Close
              </Button>
              <Button 
                variant="primary" 
                onClick={handleRestore}
                disabled={selectedAssessments.size === 0 || restoring}
              >
                {restoring ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <FaUndo className="me-2" />
                    Restore Selected ({selectedAssessments.size})
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={showConfirmation} onHide={() => setShowConfirmation(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Restoration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <FaExclamationTriangle className="me-2" />
            <strong>Are you sure?</strong>
          </Alert>
          <p>You are about to restore <strong>{selectedAssessments.size}</strong> archived assessment(s).</p>
          <p className="mb-0">
            <small className="text-muted">
              Restored assessments will become active again and appear in student reports.
            </small>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmation(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmRestore}>
            <FaUndo className="me-2" />
            Yes, Restore Now
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RestoreArchivedAssessmentsModal;